import api from '@/api/base';
import type { ApiResponse, CertificationModel } from '@/api/types';

// API URLs for certification management
const URLS = {
  certifications: '/certificate_request',
  certificationById: (id: string) => `/certificate_request/${id}`,
  uploadCertification: '/certificate_request/upload',
  downloadCertification: (id: string) => `/certificate_request/${id}/download`,
  certificationStatus: (id: string) => `/certificate_request/${id}/status`,
  commentReset: '/certificate_request/commentReset',
  stepOne: '/certificate_request/stepOne',
  incomplete: '/certificate_request/incomplete',
  facture: '/certificate_request/facture',
  complete: '/certificate_request/complete',
  checkCalculation: '/calculation_certificate/check',
  feedback: '/certificate_request/feedback',
} as const;

// Request payloads are loosely typed (the backend accepts partial request
// bodies and various admin action envelopes); responses use the shared
// ApiResponse<CertificationModel> envelope.
type CertificationPayload = Record<string, unknown>;

// Certification API methods
const certificationsApi = {
  // Get all certifications
  getCertifications: (token?: string) => {
    return api.get<ApiResponse<CertificationModel[]>>(URLS.certifications, {
      token,
    });
  },

  // Get certification by ID
  getCertificationById: (id: string, token?: string) => {
    return api.get<ApiResponse<CertificationModel>>(
      URLS.certificationById(id),
      { token },
    );
  },

  // Create new certification request
  createCertification: (
    certificationData: CertificationPayload,
    token?: string,
  ) => {
    return api.post<ApiResponse<CertificationModel>>(
      URLS.certifications,
      certificationData,
      { token },
    );
  },

  // Update certification
  updateCertification: (
    id: string,
    certificationData: CertificationPayload,
    token?: string,
  ) => {
    return api.patch<ApiResponse<CertificationModel>>(
      URLS.certificationById(id),
      certificationData,
      { token },
    );
  },

  // Delete certification
  deleteCertification: (id: string, token?: string) => {
    return api.delete<ApiResponse<null>>(URLS.certificationById(id), { token });
  },

  // Upload certification documents
  uploadCertification: (formData: FormData, token?: string) => {
    return api.post<ApiResponse<CertificationModel>>(
      URLS.uploadCertification,
      formData,
      {
        token,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  },

  // Download certification document
  downloadCertification: (id: string, token?: string) => {
    return api.get<Blob>(URLS.downloadCertification(id), {
      token,
      responseType: 'blob',
    });
  },

  // Update certification status
  updateCertificationStatus: (id: string, status: unknown, token?: string) => {
    return api.patch<ApiResponse<CertificationModel>>(
      URLS.certificationStatus(id),
      { status },
      { token },
    );
  },

  // Submit cancellation comment
  submitCancellationComment: (
    certificateId: string,
    comment: string,
    token?: string,
  ) => {
    return api.post<ApiResponse<CertificationModel>>(
      URLS.commentReset,
      { certificate_request_id: certificateId, comment },
      { token },
    );
  },

  // Accept certification (step one)
  acceptCertification: (certificateId: string, token?: string) => {
    return api.patch<ApiResponse<CertificationModel>>(
      URLS.stepOne,
      { certificate_request_id: certificateId },
      { token },
    );
  },

  // Check calculation for express service
  checkCalculation: (calculationId: string, items: unknown, token?: string) => {
    return api.patch<ApiResponse<CertificationModel>>(
      URLS.checkCalculation,
      { calculationId, items },
      { token },
    );
  },

  // Submit certification feedback
  submitFeedback: (feedbackData: CertificationPayload, token?: string) => {
    return api.post<ApiResponse<CertificationModel>>(
      URLS.feedback,
      feedbackData,
      { token },
    );
  },

  // Mark certification as incomplete (send back to client)
  markAsIncomplete: (requestData: CertificationPayload, token?: string) => {
    return api.patch<ApiResponse<CertificationModel>>(
      URLS.incomplete,
      requestData,
      { token },
    );
  },

  // Submit facture/invoice file
  submitFacture: (factureData: CertificationPayload, token?: string) => {
    return api.post<ApiResponse<CertificationModel>>(
      URLS.facture,
      factureData,
      { token },
    );
  },

  // Complete certification request
  completeRequest: (completeData: CertificationPayload, token?: string) => {
    return api.post<ApiResponse<CertificationModel>>(
      URLS.complete,
      completeData,
      { token },
    );
  },
};

export default certificationsApi;
